import { ChangeDetectionStrategy, Component, computed, effect, inject, input, signal } from '@angular/core';
import { FormControl } from '@angular/forms';
import { DateFormatService } from '@modules/common/services/date-format.service';
import { DisplayTimeZoneService } from '@modules/common/services/display-timezone.service';
import { buildInstantFromControls } from '@modules/form-controls/types/form-date-time';
import { getCurrentTimezone, headerNamesZone, zoneShortName } from '@modules/form-controls/types/timezones';
import { DateTimePickerComponent } from '../date-time-picker/date-time-picker.component';

/**
 * stitch-app-shell `TaskHeader` dates: "Start Oct 16 | Due Oct 20, 9:15 AM" under a detail title, each one opening
 * the Details tab's date & time picker on the same controls. Start can't pass Due and Due can't come before Start,
 * as the Details range picker allows (Johanna, 2026-10-21). Shared by the task and project headers.
 */
@Component({
  selector: 'stch-header-dates',
  standalone: true,
  imports: [DateTimePickerComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { class: 'header-dates', '[class.overdue]': 'overdue()' },
  templateUrl: './header-dates.component.html',
  styleUrl: './header-dates.component.less',
})
export class HeaderDatesComponent {
  public readonly fromDate = input.required<FormControl<Date>>();
  public readonly fromTime = input.required<FormControl<Date>>();
  public readonly toDate = input.required<FormControl<Date>>();
  public readonly toTime = input.required<FormControl<Date>>();
  public readonly allDay = input<boolean>(false);
  public readonly overdue = input<boolean>(false);
  /**
   * The zone the time controls' wall clock is in: a task's own zone (its editor works in it); unset, the account's
   * display zone (a project's controls hold that zone's wall clock). The header converts from it.
   */
  public readonly wallClockZone = input<string | null>(null);

  private readonly dateFormatService = inject(DateFormatService);
  private readonly displayTimeZoneService = inject(DisplayTimeZoneService);
  /** The browser's zone, which decides whether the label names a zone (a field so a spec can set it). */
  protected browserZone = getCurrentTimezone();

  /** Bumped on every change of the four controls, so the labels and limits follow edits made anywhere. */
  private readonly changes = signal(0);

  constructor() {
    effect((onCleanup) => {
      const subscriptions = [this.fromDate(), this.fromTime(), this.toDate(), this.toTime()]
        .filter(Boolean)
        .map((control) => control.valueChanges.subscribe(() => this.changes.update((count) => count + 1)));
      onCleanup(() => subscriptions.forEach((subscription) => subscription.unsubscribe()));
    });
  }

  private readonly values = computed(() => {
    this.changes();
    return {
      fromDate: asDate(this.fromDate()?.value),
      fromTime: asDate(this.fromTime()?.value),
      toDate: asDate(this.toDate()?.value),
      toTime: asDate(this.toTime()?.value),
    };
  });

  protected readonly startLabel = computed(() => this.label('Start', this.values().fromDate, this.values().fromTime));
  protected readonly dueLabel = computed(() => this.label('Due', this.values().toDate, this.values().toTime));

  /** Due draws start → the day being picked, as Details does. */
  protected readonly range = computed<[Date | null, Date | null]>(() => [this.values().fromDate, this.values().toDate]);

  /** Start can't pass Due (its time when it has one, else the end of its day). */
  protected readonly startMax = computed<Date | null>(() => {
    const { toDate, toTime } = this.values();
    if (!toDate) return null;
    if (this.allDay() || !toTime) {
      return new Date(toDate.getFullYear(), toDate.getMonth(), toDate.getDate(), 23, 59, 59, 999);
    }
    return new Date(toDate.getFullYear(), toDate.getMonth(), toDate.getDate(), toTime.getHours(), toTime.getMinutes(), 0);
  });

  /** Due can't come before Start (its time when it has one, else its day). */
  protected readonly dueMin = computed<Date | null>(() => {
    const { fromDate, fromTime } = this.values();
    if (!fromDate) return null;
    if (this.allDay() || !fromTime) return fromDate;
    return new Date(
      fromDate.getFullYear(),
      fromDate.getMonth(),
      fromDate.getDate(),
      fromTime.getHours(),
      fromTime.getMinutes(),
      0,
    );
  });

  /**
   * "Start Oct 16" for a date-only boundary (the stored day, read as the Details field reads it), else the moment in
   * the account's display zone: "Due Oct 20, 9:15 AM". So the header matches the cards and the calendar, which show
   * the account's zone (Johanna, 2026-10-22), not the item's own zone that Details edits in. That zone's short name
   * follows ("9:15 AM GMT+9") when the task's zone or the account zone reads another clock than the browser's
   * (`headerNamesZone`, Johanna 2026-10-23).
   */
  private label(word: 'Start' | 'Due', date: Date | null, time: Date | null): string {
    if (!date) {
      return `${word} date`;
    }
    if (!time || this.allDay()) {
      return `${word} ${this.dateFormatService.format(date, { format: 'monthDay' })}`;
    }
    const zone = this.displayTimeZoneService.timeZone();
    const instant = buildInstantFromControls(date, time, { floating: false, zone: this.wallClockZone() || zone });
    if (!instant) {
      return `${word} ${this.dateFormatService.format(date, { format: 'monthDay' })}`;
    }
    const day = this.dateFormatService.format(instant, { format: 'monthDay', timeZone: zone });
    const clock = this.dateFormatService.format(instant, { format: 'timeShort', timeZone: zone });
    // The zone is named only when the task's own zone or the account zone reads another clock than the browser's.
    const named = headerNamesZone(this.wallClockZone(), zone, instant, this.browserZone);
    return named ? `${word} ${day}, ${clock} ${zoneShortName(zone, instant)}` : `${word} ${day}, ${clock}`;
  }
}

function asDate(value: Date | string | null | undefined): Date | null {
  const date = value ? new Date(value) : null;
  return date && !isNaN(date.getTime()) ? date : null;
}
